// ─────────────────────────────────────────────────────────────────────────────
// <HelpTip> — the shared "?" help marker and its hover/focus popover.
//
// Helper prose that explains WHY a control exists (factor citations, code
// derivations, method rationale) costs a permanent block of 9.5px text under
// every field it documents. This collapses that prose behind the design
// system's existing 12px "?" marker (`.pl-prop-hint`, PropertyPanel/controls)
// and reuses <CodeRef>'s proven popover mechanics rather than standing up a
// second popover implementation:
//   • opens on hover after a short delay, immediately on keyboard focus
//   • click PINS it (survives pointer-leave and blur); a second click,
//     Escape, or an outside click unpins and dismisses it
//   • stays open while the pointer travels from the marker onto the card
//   • positioned by useAnchoredPopover (viewport-aware — flips and clamps)
//
// The marker is a real <button>, so the prose is reachable by keyboard and by
// tap; a native title= tooltip would be neither, and is deliberately NOT also
// set here (two tooltips on one target is worse than one good one).
//
// Nothing here is decorative: no text, no marker.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAnchoredPopover } from "../pageLayout/hooks/useAnchoredPopover";
import { helpArticleRoute } from "../help/content/manifest";
import { parseHelpTopic } from "../help/helpSurface";
import { useOpenHelpTopic } from "../help/helpTopics";

const OPEN_DELAY = 110;
const CLOSE_DELAY = 160;

export function HelpTip({
  text,
  label,
  className,
  topic,
}: {
  /** The helper prose to collapse behind the marker. */
  text?: string | null;
  /** What the prose is about — names the marker and titles the card. */
  label: string;
  className?: string;
  /** Optional help deep link ("topic-id" or "topic-id#anchor"). When present
   *  AND the topic is published, the card grows one quiet "More in Help"
   *  footer link that opens the contextual help panel on it. No topic (or an
   *  unpublished one) → the card is byte-identical to before. */
  topic?: string;
}) {
  const [open, setOpen] = useState(false);
  // Click pins the card so it survives the pointer leaving (hover-open and
  // pinned are SEPARATE states — toggling one `open` made "click to pin"
  // close the already-hover-opened card). Escape / outside click / a second
  // click clear the pin.
  const [pinned, setPinned] = useState(false);
  // One-shot guard: "More in Help" hands focus back to the marker so the help
  // panel can restore it later, but that programmatic focus must not re-open
  // the card the click just closed.
  const suppressFocusOpen = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popId = useId();
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const visible = open || pinned;
  const { popoverRef, style } = useAnchoredPopover(triggerRef, visible);

  const clearTimers = useCallback(() => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);
  const scheduleOpen = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY);
  }, []);
  const scheduleClose = useCallback(() => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Close on Escape and on outside click while visible — both clear the pin.
  useEffect(() => {
    if (!visible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setPinned(false);
        triggerRef.current?.focus();
      }
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
      setPinned(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [visible, popoverRef]);

  const openHelpTopic = useOpenHelpTopic();

  const prose = (text ?? "").trim();
  // Only a resolvable, PUBLISHED topic earns the footer link — a reserved
  // manifest slot must never produce a dead affordance.
  const helpTopic = topic && helpArticleRoute(parseHelpTopic(topic).topicId) ? topic : null;
  if (!prose) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`helptip-dot${className ? ` ${className}` : ""}`}
        aria-label={`About ${label}`}
        aria-expanded={visible}
        aria-haspopup="dialog"
        aria-controls={visible ? popId : undefined}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          if (suppressFocusOpen.current) {
            suppressFocusOpen.current = false;
            return;
          }
          setOpen(true);
        }}
        onBlur={scheduleClose}
        onClick={(event) => {
          // The marker sits inside the field's <label>; without this a click
          // would fall through and activate the control it documents.
          event.preventDefault();
          event.stopPropagation();
          clearTimers();
          // Click while pinned → unpin + close; any other click → pin open
          // (hover may already have opened the card — pinning keeps it).
          setPinned((currentlyPinned) => {
            if (currentlyPinned) {
              setOpen(false);
              return false;
            }
            setOpen(true);
            return true;
          });
        }}
      >
        {/* Hidden from the a11y tree so the glyph never leaks into the
            accessible name of the control this label names. */}
        <span aria-hidden="true">?</span>
      </button>

      {visible && createPortal(
        <div
          ref={popoverRef}
          id={popId}
          role="dialog"
          aria-label={label}
          className="helptip-pop"
          style={style}
          onMouseEnter={clearTimers}
          onMouseLeave={scheduleClose}
        >
          <span className="helptip-pop-label">{label}</span>
          <p className="helptip-pop-text">{prose}</p>
          {helpTopic && (
            <button
              type="button"
              className="helptip-pop-more"
              onClick={() => {
                clearTimers();
                setOpen(false);
                setPinned(false);
                // Hand focus back to the marker BEFORE the panel mounts: the
                // popover this button lives in is about to unmount, and the
                // panel remembers document.activeElement as the element to
                // restore focus to on close. Without this, closing the panel
                // would strand focus on <body>.
                suppressFocusOpen.current = true;
                triggerRef.current?.focus();
                openHelpTopic(helpTopic);
              }}
            >
              More in Help
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
